import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import reduce from "lodash/reduce";
import omit from "lodash/omit";
import omitBy from "lodash/omitBy";
import isNil from "lodash/isNil";
import cloneDeep from "lodash/cloneDeep";
import {
  replaceTextWithValues,
  replaceRichText,
} from "src/common/utils/text-replace";
import {
  matchDefaultCustomisation,
  matchExtraCustomisation,
} from "src/client/js/view/utils/match-customisation";
import qs from "qs";
import { localiseUrlII } from "src/common/utils/format-localised-url";
import buildProductUrl from "../../../utils/buildProductUrl";
import isPod from "../../../../../../common/utils/is-pod";

export const getFormat = (props) => {
  const customisations = props.item.customisations;
  const fallbackFormats = get(props, "fallbackProduct.cart.bookFormats", []);
  const defaultFormats =
    get(props, "product.cart.bookFormats", null) || fallbackFormats;

  // Matches cart item data for addons.
  //
  // Before we checked to see if the item had no customisations, now we check if it's premade. This resolves an issue
  // where addons can have a locale customisation. This came about as we needed to force products to have localisation
  // to handle TBOE for AU locale without creating many AU skus.
  //
  // Eagle team will need to do work to resolve addons having unneeded customisations.
  if (props.item.type === "premade") {
    const sku = get(props, "item.sku");
    const formatFromSku = defaultFormats.find(
      (defaultFormat) => defaultFormat.bookType === sku
    );
    if (formatFromSku) {
      return formatFromSku;
    }
  }

  return (
    defaultFormats
      .filter((defaultFormat) =>
        matchDefaultCustomisation(customisations, defaultFormat)
      )
      .find(
        (defaultFormat) =>
          defaultFormat.bookType === props.item.format.id &&
          matchExtraCustomisation(customisations, defaultFormat)
      ) || {}
  );
};

export const getAddons = (props) => {
  const fallbackAddons = get(
    props,
    "fallbackProduct.cart.options.cart.addOn",
    []
  );
  const defaultAddons = get(props, "product.cart.options.cart.addOn", []);

  return {
    fallbackAddons,
    defaultAddons,
  };
};

export const mergeAddons = (props) => {
  const { fallbackAddons, defaultAddons } = getAddons(props);

  return props.item.components.reduce((acc, item) => {
    let addon = {
      id: item.id,
      image: {
        url: "",
      },
      label: item.id,
    };

    const idSplit = item.id.split(":");
    const type = idSplit[0];
    const design = idSplit.pop();
    const byTypeAndDesign = (type, design) => (item) =>
      item.type === type && item.design === design;

    addon = {
      ...addon,
      ...defaultAddons.find(byTypeAndDesign(type, design)),
      ...fallbackAddons.find(byTypeAndDesign(type, design)),
    };

    if (item.id === "lmn:giftwrap") {
      addon = {
        ...addon,
        ...defaultAddons.find(byTypeAndDesign("giftwrap", "monkey")),
        ...fallbackAddons.find(byTypeAndDesign("giftwrap", "monkey")),
      };
    }

    if (addon.label !== item.id) {
      acc.push(addon);
    }

    return acc;
  }, []);
};

export const getTitle = (props) => {
  const format = getFormat(props);

  if (format.title) {
    return format.title;
  }

  const fallbackTitle = get(
    props,
    "fallbackProduct.productInformation.title",
    props.item.sku
  );
  return get(props, "product.productInformation.title", null) || fallbackTitle;
};

export const getCustomisations = ({ item: { customisations } }) => {
  if (isEmpty(customisations)) {
    return null;
  }
  // Not components as in addons, which is found on item.components. item.customisations.components
  // is always empty
  return omit(customisations, ["components"]);
};

export const getCustomisationString = (props) => {
  const fallbackCustomisation = get(
    props,
    "fallbackProduct.cart.customisation",
    ""
  );
  const customisationString =
    get(props, "product.cart.customisation", null) || fallbackCustomisation;
  const customisationsObject = getCustomisations(props);
  return replaceTextWithValues(customisationString, customisationsObject);
};

export const getType = (props) => {
  const cartFormatId = props.item.format.id;
  const fallbackBookTypes = get(
    props,
    "product.cart.options.cart.bookType",
    []
  );
  const bookTypes =
    get(props, "product.cart.options.cart.bookType", null) || fallbackBookTypes;
  const bookType = bookTypes.find((book) => book.bookType === cartFormatId);
  return get(bookType, "label", cartFormatId);
};

export const getExpandedCustomisation = (props, addons = []) => {
  const customisationTranslations = get(
    props,
    "model.prismicGlobalContentModel.orders.customisation",
    []
  );
  const customisations = omit(props.item.customisations, [
    "box",
    "components",
    "cover",
  ]);
  if (isEmpty(customisations)) {
    return [];
  }

  const locale = customisations.locale;
  let translatedLanguage = locale;

  // Getting translated language by locale
  if (get(props, "product.response.ok", false)) {
    const formBody = get(props, "product.form.body", []);
    if (!isEmpty(formBody)) {
      const localeSliceType = formBody.find(
        (item) => item.sliceType === "locale"
      );
      if (localeSliceType) {
        // there's no guarantee that localSliceTypeItem will be found
        const localSliceTypeItem = localeSliceType.items.find(
          (item) => item.optionValue === locale
        );
        if (localSliceTypeItem) {
          translatedLanguage = localSliceTypeItem.optionTitle;
        }
      }
    }
  }

  // Getting translated customisations
  const translatedCustomisations = reduce(
    customisations,
    (result, value, key) => {
      if (result[key]) {
        return result;
      }
      const translation =
        customisationTranslations.find((item) => {
          return item.id && item.id.includes(key) && item.id.includes(value);
        }) || {};
      result[key] = translation.label || value;
      if (key === "locale") {
        result.locale = translatedLanguage;
        result.customisationLocale = translatedLanguage;
      }
      return result;
    },
    {}
  );

  const fallbackCustomisation = get(
    props,
    "fallbackProduct.cart.expandedCustomisation",
    []
  );
  const customisation = get(props, "product.cart.response.ok", false)
    ? get(props, "product.cart.expandedCustomisation", fallbackCustomisation)
    : fallbackCustomisation;
  const filteredCustomisation =
    addons.length > 0
      ? customisation
      : customisation.filter((c) => c.text !== "%{addons}");
  const customisationAddon = addons.map((addon) => addon.label).join(" + ");
  return replaceRichText(filteredCustomisation, {
    ...translatedCustomisations,
    addons: customisationAddon,
    formatIdentifier: getType(props),
  });
};

export const buildCustomisationsFromItem = (item) =>
  omitBy(
    {
      ...(item.customisation || item.customisations),
      components: item.components,
      // item.eagleFormatIdentifier is only assigned if the type of page url is /formats.
      // item.format object doesn't exist if the type of page url is /formats.
      format_identifier:
        get(item, "format.id") ||
        item.eagleFormatIdentifier ||
        item.formatIdentifier,
    },
    isNil
  );

export const buildEditUrl = ({ item, ...props }, type = "preview") => {
  const orderNumber = props.number || props.orderNumber;
  const newItem = cloneDeep(item);

  // At the moment both format.id and format.identifier are provided by Eagle.
  // When building a url for the formats page, we only need the latest format.identifier
  // value from Eagle.
  // format.id will be deprecated in the future. Rather than change buildCustomisationsFromItem
  // to use different identifiers based on the type of page url that needs to be generated,
  // we decided to assign an eagleFormatIdentifier prop and remove the item.format object.
  if (type === "formats") {
    newItem.eagleFormatIdentifier = newItem.format.identifier;
    delete newItem.format;
  }

  const productCustomisation = buildCustomisationsFromItem(newItem);
  const query = qs.stringify(productCustomisation, { addQueryPrefix: true });
  // Pass information from LineItemII in cart || OrderButton in account/orders
  const countryModel = get(props, "model.countryModel") || props.country;

  // Construct PDC proxied URL to edit product from cart and account/orders pages.
  // Edit product or format from cart page
  let url = `/personalized-products/${newItem.productSlug}/edit/${type}/cart/${newItem.id}`;

  // Edit product from account/orders page
  // Should only be allowed to edit the preview page from account
  // Check if order_id already exists in the query before adding it
  if (orderNumber && type === "preview") {
    url = `/personalized-products/${newItem.productSlug}/edit/${type}/order/${orderNumber}/${newItem.id}`;
  }

  // Edit addons and giftwraps from cart page directs to PDC edit routes.
  if (type === "add-ons" || type === "giftwraps") {
    url = `/personalized-products/${newItem.productSlug}/edit/${type}/cart/${newItem.id}`;
  }

  // The EditableLinks in LineItemII do not use the following code.
  if (type === "options") {
    url = `/personalized-products/${newItem.productSlug}/${type}/${newItem.id}${query}`;
  }

  return localiseUrlII(countryModel, url);
};

// Find giftwrap addon page. If there's a giftwrap on it take the first
// giftwrap else return empty array.
export const getAvailableGiftwraps = (addon) => {
  if (typeof addon?.giftwrapPageIdx !== "number") {
    return [];
  }
  return addon.pages[addon.giftwrapPageIdx].items;
};

export const giftwrapCta = (props) => {
  const addon = props.item.itemAddonOptions;
  if (addon && addon.giftwrapPageIdx !== null) {
    //refactor to a param
    return {
      edit: props.editGiftwrapCta,
      add: props.addGiftwrapCta,
      url: buildProductUrl(props, {
        type: "options",
        itemId: props.item.id,
        queryParams: omitBy(
          {
            ...buildCustomisationsFromItem(props.item),
            addonPage: addon.giftwrapPageIdx + 1,
          },
          isNil
        ),
      }),
    };
  }
};

export const extrasCta = (props) => {
  const addon = props.item.itemAddonOptions;

  if (addon && addon.extrasPageIdx !== null) {
    return {
      edit: props.editExtrasCta,
      add: props.addExtrasCta,
      url: buildProductUrl(props, {
        type: "options",
        itemId: props.item.id,
        queryParams: {
          ...buildCustomisationsFromItem(props.item),
          addonPage: addon.extrasPageIdx + 1,
        },
      }),
    };
  }
};

// what a mess the cart truly is...
export const rebuildAddonAndExtaEditUrl = (props) => {
  props.updateGiftwrapCta({
    edit: props.editGiftwrapCta,
    add: props.addGiftwrapCta,
    url: buildProductUrl(props, {
      type: "options",
      itemId: props.item.id,
      queryParams: omitBy(
        {
          ...buildCustomisationsFromItem(props.item),
        },
        isNil
      ),
    }),
  });

  if (props.item.itemAddonOptions?.extrasPageIdx) {
    props.updateExtraCta({
      edit: props.editExtrasCta,
      add: props.addExtrasCta,
      url: buildProductUrl(props, {
        type: "options",
        itemId: props.item.id,
        queryParams: {
          ...buildCustomisationsFromItem(props.item),
          addonPage: props.item.itemAddonOptions.extrasPageIdx + 1,
        },
      }),
    });
  }
};

export const allowEdit = (props) => {
  if (props.type && !isPod(props)) {
    return false;
  }

  if (props.state && props.state !== "complete") {
    return false;
  }

  // Currently my-golden-ticket and the-littlest-bear removes existing
  // customisations when editing, for this reason we have temporarily removed
  // the possibility to edit them after adding to cart.
  switch (props.productId) {
    case "my-golden-ticket":
    case "littlest-bear":
      return false;
  }

  return true;
};
